import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap, filter, skipWhile, first } from 'rxjs/operators';

export interface PromoBannerData
{
    procent: string;
    tekst1: string;
    tekst2: string;
    kod: string;
    dataKonca: Date;
    dataPoczatku?: Date;
    importers?: string[] | null;
}

interface PromoBannerDataFromApi
{
    procent: string;
    tekst1: string;
    tekst2: string;
    kod: string;
    dataKonca: string;
    dataPoczatku?: string;
    importers?: string[];
}

@Injectable({
    providedIn: 'root'
})
export class PromotionService
{
    private readonly API_URL = 'https://images.arte.pl/svg/Timer/promo-timer-cz.json';
    private promotionsSubject: BehaviorSubject<{ promotions: PromoBannerData[], loaded: boolean }> =
        new BehaviorSubject<{ promotions: PromoBannerData[], loaded: boolean }>({ promotions: [], loaded: false });
    private loadingInProgress: boolean = false;

    constructor(private httpClient: HttpClient) { }

    /**
     * Zwraca aktywną promocję dla danego dnia
     * @param date Data dla której sprawdzamy promocję (domyślnie dzisiaj)
     * @returns Observable<PromoBannerData | null>
     */
    public getActivePromotion(date: Date = new Date()): Observable<PromoBannerData | null>
    {
        const currentState = this.promotionsSubject.value;

        // Jeśli promocje nie zostały jeszcze załadowane i nie trwa ładowanie, pobierz je
        if (!currentState.loaded && !this.loadingInProgress)
        {
            this.loadPromotions();
        }

        return this.promotionsSubject.pipe(
            skipWhile(state => !state.loaded), // Pomiń emisje dopóki dane nie zostaną załadowane
            map(state =>
            {
                const promotions = state.promotions;

                // Znajdź promocję aktywną dla podanej daty
                const activePromotion = promotions.find(promo =>
                {
                    try
                    {
                        const startDate = promo.dataPoczatku || promo.dataKonca;
                        const start = new Date(startDate);
                        if (isNaN(start.getTime()))
                        {
                            return false;
                        }
                        start.setHours(0, 0, 0, 0);

                        const end = new Date(promo.dataKonca);
                        if (isNaN(end.getTime()))
                        {
                            return false;
                        }
                        end.setHours(23, 59, 59, 999);

                        const checkDate = new Date(date);
                        if (isNaN(checkDate.getTime()))
                        {
                            return false;
                        }
                        checkDate.setHours(0, 0, 0, 0);

                        return checkDate >= start && checkDate <= end;
                    }
                    catch (error)
                    {
                        console.error('Error checking promotion date:', error, promo);
                        return false;
                    }
                });

                return activePromotion || null;
            }),
            first() // Weź pierwszą wartość po załadowaniu
        );
    }

    /**
     * Ładuje promocje z API
     */
    private loadPromotions(): void
    {
        this.loadingInProgress = true;

        this.httpClient.get<PromoBannerDataFromApi[]>(this.API_URL).pipe(
            map((data: PromoBannerDataFromApi[]) =>
            {
                // Walidacja: sprawdź czy data jest tablicą
                if (!Array.isArray(data))
                {
                    console.error('Promotions data is not an array:', data);
                    return [];
                }

                // Konwertuj dane z API na format PromoBannerData z obsługą błędów
                const validPromotions: PromoBannerData[] = [];
                for (const item of data)
                {
                    try
                    {
                        const promo = this.convertApiDataToPromoBannerData(item);
                        // Sprawdź czy data jest poprawna
                        if (!isNaN(promo.dataKonca.getTime()))
                        {
                            validPromotions.push(promo);
                        }
                    }
                    catch (error)
                    {
                        console.error('Error converting promotion data:', error, item);
                        // Pomijamy niepoprawne elementy
                    }
                }
                return validPromotions;
            }),
            catchError(error =>
            {
                console.error('Error loading promotions:', error);
                // Zwróć pustą tablicę w przypadku błędu
                return of<PromoBannerData[]>([]);
            }),
            tap(promotions =>
            {
                this.promotionsSubject.next({ promotions, loaded: true });
                this.loadingInProgress = false;
            })
        ).subscribe();
    }

    /**
     * Konwertuje dane z API na format PromoBannerData
     */
    private convertApiDataToPromoBannerData(apiData: PromoBannerDataFromApi): PromoBannerData
    {
        // Walidacja wymaganych pól
        if (!apiData || !apiData.dataKonca)
        {
            throw new Error('Invalid promotion data: missing required fields');
        }

        const dataKonca = new Date(apiData.dataKonca);
        const dataPoczatku = apiData.dataPoczatku ? new Date(apiData.dataPoczatku) : undefined;

        // Sprawdź czy data końca jest poprawna
        if (isNaN(dataKonca.getTime()))
        {
            throw new Error(`Invalid date format: ${apiData.dataKonca}`);
        }

        return {
            procent: apiData.procent || '',
            tekst1: apiData.tekst1 || '',
            tekst2: apiData.tekst2 || '',
            kod: apiData.kod || '',
            dataKonca: dataKonca,
            dataPoczatku: dataPoczatku && !isNaN(dataPoczatku.getTime()) ? dataPoczatku : undefined,
            importers: apiData.importers !== undefined ? (Array.isArray(apiData.importers) ? apiData.importers : []) : null
        };
    }

    /**
     * Zwraca wszystkie promocje (użyteczne do debugowania lub przyszłej funkcjonalności)
     */
    public getAllPromotions(): Observable<PromoBannerData[]>
    {
        const currentState = this.promotionsSubject.value;

        // Jeśli promocje nie zostały jeszcze załadowane, pobierz je
        if (!currentState.loaded && !this.loadingInProgress)
        {
            this.loadPromotions();
        }

        return this.promotionsSubject.pipe(
            skipWhile(state => !state.loaded),
            map(state => state.promotions),
            first()
        );
    }
}

